import React from "react";
import { Button, CircularProgress, Typography, useTheme } from "@mui/material";
import AdaptiveDialog, { dialogPrimaryButtonSx, dialogQuietButtonSx } from "./AdaptiveDialog";
import { useTouchLayout } from "../../hooks/useTouchLayout";
import { hapticImpact } from "../../lib/haptics";

interface AdaptiveConfirmProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Confirmation for a destructive action.
 *
 * Replaces window.confirm, which must never be used in this app: a native
 * modal dialog blocks every subsequent event in the Capacitor WebView, which
 * hard-freezes the iOS app.
 */
const AdaptiveConfirm: React.FC<AdaptiveConfirmProps> = ({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  loading = false,
  onConfirm,
  onCancel,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isTouch = useTouchLayout();

  const handleConfirm = () => {
    hapticImpact();
    onConfirm();
  };

  return (
    <AdaptiveDialog
      open={open}
      onClose={loading ? () => {} : onCancel}
      title={title}
      maxWidth="xs"
      actions={
        <>
          <Button
            onClick={handleConfirm}
            disabled={loading}
            variant="contained"
            fullWidth={isTouch}
            disableElevation
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{
              ...dialogPrimaryButtonSx(isDark, isTouch),
              ...(destructive && {
                backgroundColor: theme.palette.error.main,
                "&:hover": { backgroundColor: "#dc2626", boxShadow: "none" },
              }),
            }}
          >
            {confirmLabel}
          </Button>
          <Button
            onClick={onCancel}
            disabled={loading}
            fullWidth={isTouch}
            sx={dialogQuietButtonSx(isDark, isTouch)}
          >
            {cancelLabel}
          </Button>
        </>
      }
    >
      <Typography sx={{ fontSize: "0.85rem", lineHeight: 1.5, color: theme.palette.text.secondary }}>
        {message}
      </Typography>
    </AdaptiveDialog>
  );
};

export default AdaptiveConfirm;
